import { createReducer, on } from '@ngrx/store';
import * as DossierActions from './dossier.actions';
import { initialDossierState } from './dossier.state';

export const dossierReducer = createReducer(
  initialDossierState,

  // Load Erfassung
  on(DossierActions.loadErfassung, (state, { erfassungRef }) => ({
    ...state,
    erfassungId: erfassungRef.erfassungId,
    isLoading: true,
    isDeleting: false,
    isDeleted: false,
    submitIsBusy: false,
  })),

  on(DossierActions.loadErfassungSuccess, (state, { dossier }) => ({
    ...state,
    isLoading: false,
    dossier,
    renderState: new Map(),
    renderingItems: 0,
  })),

  on(DossierActions.loadErfassungError, state => ({
    ...state,
    isLoading: false,
  })),

  // Load Substance/Dossier
  on(DossierActions.loadSubstance, (state, { loadSubstance }) => ({
    ...state,
    id: loadSubstance.id,
    isLoading: true,
    isDeleting: false,
    isDeleted: false,
    submitIsBusy: false,
  })),

  on(DossierActions.loadSubstanceSuccess, (state, { dossier }) => ({
    ...state,
    isLoading: false,
    dossier,
    renderState: new Map(),
    renderingItems: 0,
  })),

  on(DossierActions.loadSubstanceError, state => ({
    ...state,
    isLoading: false,
    dossier: null,
    isDeleted: true,
  })),

  // Loading spinner
  on(DossierActions.showSmallLoadingSpinner, (state, { isInProgress }) => ({
    ...state,
    isPartialUpdateInProgress: isInProgress,
  })),

  // Partial update
  on(DossierActions.partialUpdateDossier, (state, { updatedDossier }) => ({
    ...state,
    dossier: updatedDossier,
    renderState: new Map(),
    renderingItems: 0,
  })),

  // Clear render state
  on(DossierActions.clearRenderState, state => ({
    ...state,
    renderState: new Map(),
    renderingItems: 0,
  })),

  // Role context
  on(DossierActions.setRoleContext, (state, { role }) => ({
    ...state,
    roleContext: role,
  })),

  // Re-edit dossier
  on(DossierActions.reEditDossier, state => ({
    ...state,
    isReEditing: true,
  })),

  on(DossierActions.reEditDossierSuccess, state => ({
    ...state,
    isReEditing: false,
  })),

  on(DossierActions.reEditDossierError, state => ({
    ...state,
    isReEditing: false,
  })),

  // Withdraw erfassung
  on(DossierActions.withdrawErfassung, state => ({
    ...state,
  })),

  // Take erfassung
  on(DossierActions.takeErfassung, state => ({
    ...state,
    isTaking: true,
  })),

  on(DossierActions.takeErfassungSuccess, state => ({
    ...state,
    isTaking: false,
  })),

  on(DossierActions.takeErfassungError, state => ({
    ...state,
    isTaking: false,
  })),

  // Render state
  on(DossierActions.setRenderState, (state, { panelId, renderState }) => {
    const clonedState = new Map(state.renderState);
    clonedState.set(panelId, renderState);
    return {
      ...state,
      renderState: clonedState,
      renderingItems: Math.max(0, state.renderingItems + (renderState === 'rendered' ? -1 : 1)),
    };
  }),

  // Search state
  on(DossierActions.setSearchState, (state, { search, hits, isWholeWord, isIgnoreCase, state: searchState }) => ({
    ...state,
    searchState: {
      ...state.searchState,
      search,
      hits,
      isWholeWord,
      isIgnoreCase,
      currentHit: null,
      state: searchState,
    },
  })),

  on(DossierActions.setSearchStateState, (state, { searchState }) => ({
    ...state,
    searchState: {
      ...state.searchState,
      state: searchState,
    },
  })),

  on(DossierActions.gotoSearchResult, (state, { next }) => ({
    ...state,
    searchState: {
      ...state.searchState,
      currentHit: next,
    },
  })),

  // Submit substance
  on(DossierActions.submitSubstance, state => ({
    ...state,
    submitIsBusy: true,
  })),

  on(DossierActions.submitSubstanceSuccess, state => ({
    ...state,
    submitIsBusy: false,
  })),

  on(DossierActions.submitSubstanceError, state => ({
    ...state,
    submitIsBusy: false,
  })),

  // Copy Sachverhalte
  on(DossierActions.setCopySachverhalteDialogLoading, (state, { isLoading }) => ({
    ...state,
    copySachverhalte: {
      ...state.copySachverhalte,
      isLoading,
    },
  })),

  on(DossierActions.showCopySachverhalteDialog, (state) => ({
    ...state,
    copySachverhalte: {
      dossierIds: [],
      isLoading: false,
    },
  })),

  // Thesaurus mode
  on(DossierActions.setThesaurusMode, (state, { thesaurusMode }) => ({
    ...state,
    thesaurusMode,
  })),

  // Sachverhalt Toggle
  on(DossierActions.showSachverhaltToggle, (state, { mode }) => ({
    ...state,
    sachverhaltToggle: {
      isVisible: true,
      mode,
      selectedSachverhalte: [],
    },
  })),

  on(DossierActions.hideSachverhaltToggle, state => ({
    ...state,
    sachverhaltToggle: {
      isVisible: false,
      mode: null,
      selectedSachverhalte: [],
    },
  })),

  on(DossierActions.addSachverhaltToSelection, (state, { sachverhaltId, erfassungSachverhaltId, merkmalName, merkmalId }) => {
    // Add to selection (validation already done in effect)
    return {
      ...state,
      sachverhaltToggle: {
        ...state.sachverhaltToggle,
        selectedSachverhalte: [
          ...state.sachverhaltToggle.selectedSachverhalte,
          { sachverhaltId, erfassungSachverhaltId, merkmalName, merkmalId }
        ],
      },
    };
  }),

  on(DossierActions.removeSachverhaltFromSelection, (state, { sachverhaltId, erfassungSachverhaltId }) => {
    // Remove from selection
    return {
      ...state,
      sachverhaltToggle: {
        ...state.sachverhaltToggle,
        selectedSachverhalte: state.sachverhaltToggle.selectedSachverhalte.filter(
          s => !(s.sachverhaltId === sachverhaltId && s.erfassungSachverhaltId === erfassungSachverhaltId)
        ),
      },
    };
  }),

  // Assignable Users
  on(DossierActions.loadAssignableUsers, (state) => ({
    ...state,
    assignableUsers: {
      isLoading: true,
      users: [], // Reset to empty array, component will show unassigned item
      error: null,
    },
  })),

  on(DossierActions.loadAssignableUsersSuccess, (state, { users }) => ({
    ...state,
    assignableUsers: {
      isLoading: false,
      users,
      error: null,
    },
  })),

  on(DossierActions.loadAssignableUsersError, (state, { error }) => ({
    ...state,
    assignableUsers: {
      ...state.assignableUsers,
      isLoading: false,
      error,
    },
  }))
);
